import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { difference } from 'underscore';

import { showAlert } from '@general-ui/alert/alert-service';
import { useProjectDetailsEnabled } from '@admin/events/hooks/project-details';
import { useEventUuidParam } from 'utils/use-event-uuid-param';
import indexedDb from 'utils/datastore/indexed-db';
import { useTypedSelector } from 'store/reducers/use-typed-selector';
import { Channel, EPermissions } from 'types/working-model';
import { setChangingChannels, setUser } from 'store/actions/authentication';
import { SelectChannel } from 'connection/auth';
import { setSelectedEventGroup } from 'store/actions/admin/events';
import { resetTemplateEvents } from 'store/actions/admin/create-event';
import { LAST_ACCESSED_CHANNELS } from '../constants';

interface LastAccessedChannel {
	channelId: number;
	accessTime: number;
}

interface UseChannelSelectOptions {
	onChannelSelectSuccess?: (destinationChannel: Channel) => void;
	doSort?: boolean;
}

export const useChannelSelect = ({ onChannelSelectSuccess, doSort = false }: UseChannelSelectOptions) => {
	const dispatch = useDispatch();

	const user = useTypedSelector(state => state.AuthReducer.user);
	const channels = useTypedSelector(state => state.AuthReducer.channels);
	const changingChannels = useTypedSelector(state => state.AuthReducer.changingChannels);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const eventsLoading = useTypedSelector(state => state.EventsReducer.eventsLoading);

	const [lastAccessedChannels, setLastAccessedChannels] = useState<LastAccessedChannel[]>([]);
	const [willReload, setWillReload] = useState(false);

	const initialLastAccessedChannelsRef = useRef<LastAccessedChannel[]>([]);

	const projectDetailsFlagOn = useProjectDetailsEnabled();
	const eventUuid = useEventUuidParam();

	// retrieve sorted channels
	useEffect(() => {
		const fetchLastAccessedChannels = async () => {
			try {
				const stored = await indexedDb.getItem<LastAccessedChannel[]>(LAST_ACCESSED_CHANNELS, eventUuid);
				initialLastAccessedChannelsRef.current = stored || [];
				setLastAccessedChannels(stored || []);
			} catch (err) {
				console.error('Failed to fetch LAST_ACCESSED_CHANNELS from IndexedDB', err);
				setLastAccessedChannels([]); // Ensure state is initialized even on error
			}
		};
		if (doSort) {
			fetchLastAccessedChannels();
		}
	}, [doSort, eventUuid]);

	// store sorted channels
	useEffect(() => {
		const saveLastAccessedChannels = async () => {
			try {
				await indexedDb.putItem({
					uuid: eventUuid,
					type: LAST_ACCESSED_CHANNELS,
					data: lastAccessedChannels,
				});
			} catch (error) {
				console.error('Failed to save LAST_ACCESSED_CHANNELS to IndexedDB', error);
			}
		};

		if (doSort && (lastAccessedChannels.length > 0 || initialLastAccessedChannelsRef.current.length > 0)) {
			saveLastAccessedChannels();
		}
	}, [doSort, eventUuid, lastAccessedChannels]);

	const sortedChannels = useMemo(() => {
		const accessMap = new Map<number, number>(
			initialLastAccessedChannelsRef.current.map(item => [item.channelId, item.accessTime])
		);

		return [...channels].sort((a, b) => {
			const aTime = accessMap.get(a.channel);
			const bTime = accessMap.get(b.channel);

			if (aTime && bTime) {
				return bTime - aTime;
			}
			if (aTime) {
				return -1;
			}
			if (bTime) {
				return 1;
			}
			return 0;
		});
	}, [channels]);

	const selectChannel = useCallback(async (destinationChannel: Channel) => {
		if (changingChannels || eventsLoading || !user) return;

		const channelId = destinationChannel.channel;
		if (channelId === user?.active_channel) return;

		const isOnlyPresenterOnChannel = difference(
			user?.channels[destinationChannel.channel],
			[EPermissions.Presenter]
		).length === 0;

		if (isOnlyPresenterOnChannel) {
			showAlert({
				message: 'Presenter only',
				description: `You have presenter only access in this channel. We've opened Greenroom in a new tab.`,
				extraContent: (
					<div className="alert-buttons">
						<a
							className="theme-btn link-btn"
							href={process.env.REACT_APP_GREENROOM_FRONTEND_URL}
							rel="noreferrer"
							target="_blank"
						>
							Click here to go to Greenroom
						</a>
					</div>
				),
				duration: 10000,
			});
			return;
		}

		// Email / password path
		if (token) {
			setWillReload(true);

			try {
				dispatch(setChangingChannels(true));
				const updatedToken = await SelectChannel(token, channelId);
				dispatch(setUser(updatedToken));
				dispatch(setSelectedEventGroup(''));
				dispatch(resetTemplateEvents());

				const now = Date.now();
				setLastAccessedChannels(prev => {
					const existingIndex = prev.findIndex(item => item.channelId === channelId);
					if (existingIndex > -1) {
						return prev.map((item, index) =>
							index === existingIndex ? { ...item, accessTime: now } : item
						);
					}
					return [...prev, { channelId, accessTime: now }];
				});

				if (onChannelSelectSuccess) {
					onChannelSelectSuccess(destinationChannel);
				} else {
					location.reload();
				}
			} catch (error) {
				console.error('Failed to select channel:', error);
				showAlert({ message: 'Error', description: 'Failed to switch channels. Please try again.' });
				dispatch(setChangingChannels(false));
				setWillReload(false);
			}
		}
	}, [
		changingChannels,
		eventsLoading,
		user,
		token,
		dispatch,
		onChannelSelectSuccess
	]);
	return {
		sortedChannels,
		willReload,
		selectChannel,
		setLastAccessedChannels,
	};
};
